import { AssetStatus, ProductTypeName } from '@electricjs/core_entity-client';
import {
  CUSTOMER_SITE_DEPLOY_COUNTRIES,
  WAREHOUSE_DEPLOY_COUNTRIES,
} from '@/constants/assets';
import { ROUTES } from '@/constants/routes';
import { Asset } from '@/types/assets';
import { SelectionParamKeys } from '@/types/queryParamKeys';

export const LaptopAction = {
  Retrieve: 'retrieve',
  DeployFromOrganizationSite: 'deployFromOrganizationSite',
  /** Same label as DeployFromOrganizationSite, but the destination needs an employee, which the user picks first */
  DeployFromWarehouse: 'deployFromWarehouse',
} as const;

export type LaptopAction = (typeof LaptopAction)[keyof typeof LaptopAction];

export type LaptopActionContext = {
  /** The asset's site's country. An unknown country can't be confirmed, so nothing can be deployed from it */
  siteCountry?: string;
  /** The assignee is outside the user's divisions: retrieval targets the employee by id, which the user can't select */
  isAssigneeHidden?: boolean;
};

export type LaptopActionLink = {
  action: LaptopAction;
  url: string;
};

/**
 * Decides which laptop shipping action (if any) an asset offers, and where it links to.
 * A "customer site" is any non-global site (it belongs to an organization); the warehouse is a global site.
 * Assets can't be deployed from outside the US and Canada (customer sites) or the US (warehouse), and an unknown country can't be confirmed.
 */
export const getLaptopActionLink = (
  asset?: Asset,
  { siteCountry, isAssigneeHidden }: LaptopActionContext = {}
): LaptopActionLink | undefined => {
  if (asset?.productType?.name !== ProductTypeName.Laptop) return undefined;

  const isAtGlobalSite = !!asset.site && !asset.site.organizationId;

  const isAssignedToHiddenEmployee =
    !!asset.assignedToEmployee && !!isAssigneeHidden;

  if (
    asset.status === AssetStatus.InUse &&
    !isAtGlobalSite &&
    !isAssignedToHiddenEmployee
  ) {
    // Held by an employee: retrieve from them. Otherwise it's held at a customer site: retrieve from the site.
    const searchParams = new URLSearchParams({
      ...(asset.assignedToEmployee && {
        [SelectionParamKeys.EmployeeId]: asset.assignedToEmployee.id,
      }),
      [SelectionParamKeys.AssetId]: asset.id,
    });
    if (asset.assignedToEmployee || asset.site) {
      return {
        action: LaptopAction.Retrieve,
        url: `${ROUTES.SHIP_LAPTOP}?${searchParams.toString()}`,
      };
    }
  }

  if (asset.status === AssetStatus.Available && asset.site) {
    const deployCountries = isAtGlobalSite
      ? WAREHOUSE_DEPLOY_COUNTRIES
      : CUSTOMER_SITE_DEPLOY_COUNTRIES;
    if (!deployCountries.includes(siteCountry?.toUpperCase() ?? '')) {
      return undefined;
    }

    // Warehoused assets are deployed through the storefront, with the asset already in the cart and the employee picked beforehand (the URL needs `employeeId` appended)
    if (isAtGlobalSite) {
      const searchParams = new URLSearchParams({
        [SelectionParamKeys.CartAssetId]: asset.id,
      });
      return {
        action: LaptopAction.DeployFromWarehouse,
        url: `${ROUTES.GET_HARDWARE}?${searchParams.toString()}`,
      };
    }

    const searchParams = new URLSearchParams({
      [SelectionParamKeys.AssetId]: asset.id,
    });
    return {
      action: LaptopAction.DeployFromOrganizationSite,
      url: `${ROUTES.SHIP_LAPTOP}?${searchParams.toString()}`,
    };
  }

  return undefined;
};
